import React from 'react'
import * as Sentry from '@sentry/nextjs'
import ListingWrapper, { ListingHeader, ListingFeaturedContent } from 'core/styles/Listing'
import Grid, { Cell } from 'core/styles/Grid'
import Render from 'core/layout/Render'
import Search, { SearchWrapper } from 'core/layout/Search'
import { useRouter } from 'next/router'
import {
  Filter,
  FilterAction,
  FilterActions,
  FilterContainer,
  FilterGroup,
  FilterList,
  FilterMobile,
  FilterOption,
  FilterOverlay
} from 'core/styles/Filter'
import { Button } from 'core/layout/Common'
import { Tab, Tabs } from 'core/styles/Tabs'
import { Splash } from 'core/styles/Banner'
import CollapsablePanel from './CollapsablePanel'
import NoSSR from 'core/helpers/nossr'
import getConfig from 'next/config'
import FeaturedRecipe from './FeaturedRecipe'
import FeaturedProducts from './FeaturedProducts'
import FeaturedContent from './FeaturedContent'
import ReactHtmlParser from 'react-html-parser'

interface KeywordRedirection {
  keyword: string;
  replacement: string;
}

const Listing = (props: any) => {
  const router = useRouter()
  const [filters, setFilters] = React.useState<any>([])
  const [checkedFilters, setCheckedFilters] = React.useState<any>({})
  const [page, setPage] = React.useState<number>(1)
  const [filterOpen, setFilterOpen] = React.useState<boolean>(false)
  const [fetchedItems, setFetchedItems] = React.useState<any[] | null>(null)
  const [isFetchingItems, setIsFetchingItems] = React.useState<boolean>(false)
  const [isFilterNavigating, setIsFilterNavigating] = React.useState<boolean>(false)
  const { keyword } = router.query
  const search = keyword || 'keyword' in router.query
  const { publicRuntimeConfig } = getConfig()
  const featured_recipe = props?.result?.featured_recipe?.[0]
  const featured_content = props?.result?.featured_content
  const unprocessedText = 'unprocessed'
  const locale = props?.result?.locale

  // Clear isFilterNavigating when the route change completes or errors.
  // This ensures the "no results" message is suppressed only during the
  // brief window between filter selection and new props arriving.
  React.useEffect(() => {
    const done = () => setIsFilterNavigating(false)
    router.events.on('routeChangeComplete', done)
    router.events.on('routeChangeError', done)
    return () => {
      router.events.off('routeChangeComplete', done)
      router.events.off('routeChangeError', done)
    }
  }, [router.events])

  // Lower the header z-index when the mobile filter overlay is open so it
  // doesn't paint over the fixed-position overlay (stacking-context fix).
  React.useEffect(() => {
    if (filterOpen) {
      document.documentElement.classList.add('filter-open')
    } else {
      document.documentElement.classList.remove('filter-open')
    }
    return () => {
      document.documentElement.classList.remove('filter-open')
    }
  }, [filterOpen])

  // Phase 2: Client-side fetch for full product catalog.
  // When fetchUrl is provided, the component fetches the complete item list on mount,
  // replacing the initial SSG batch (props.items) with the full dataset.
  // When fetchUrl is absent, behaviour is unchanged (uses props.items directly).
  React.useEffect(() => {
    if (!props.fetchUrl) return
    const controller = new AbortController()
    setIsFetchingItems(true)
    fetch(props.fetchUrl, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        if (controller.signal.aborted) return
        const items = data?.Results || []
        // Sort alphabetically by short_title to match SSG sort order
        items.sort((a: any, b: any) =>
          !a.short_title ? 1 : !b.short_title ? -1 : a.short_title.localeCompare(b.short_title)
        )
        setFetchedItems(items)
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return
        console.error('Failed to fetch product listing:', err)
        Sentry.captureException(err)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsFetchingItems(false)
        }
      })
    return () => {
      controller.abort()
    }
  }, [props.fetchUrl])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- router.push is stable; including the
  // full router object would cause this effect to re-fire on every render since Next.js 12
  // returns a new router reference on each render. Only keyword changes should trigger redirects.
  React.useEffect(() => {
    if (publicRuntimeConfig?.keywordRedirections) {
      const redirections: KeywordRedirection[] = publicRuntimeConfig.keywordRedirections;
      const lowerCaseKeyword = keyword?.toString()?.toLowerCase();

      const matchedRedirection = redirections.find(
        (redirection: KeywordRedirection) => redirection.keyword.toLowerCase() === lowerCaseKeyword
      );

      if (matchedRedirection) {
        router.push(
          `/search?type=${props.prefix}&keyword=${matchedRedirection.replacement}`,
          undefined,
          { scroll: true, shallow: false }
        );
      }
    }
  }, [keyword]) // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    setFilters([])
  }, [keyword])

  const resultUid = props.result?.uid
  const filterIdQuery = router.query?.filterId as string | undefined
  const slugQuery = router.query?.slug?.[0] as string | undefined

  React.useEffect(() => {
    const values = ((filterIdQuery) || (slugQuery) || '')
      .split(':')
      .filter((v: any) => v)

    if (values.length === 0) return

    const filterValues = props.result?.filters
      ?.map((group: any) => ({
        key: group.title,
        label: group.items.find((item: any) => values.indexOf(item.value) > -1)?.title,
        value: group.items.find((item: any) => values.indexOf(item.value) > -1)?.value
      }))
      .filter((filter: any) => filter.value)

    setFilters(filterValues)
  }, [resultUid, filterIdQuery, slugQuery])

  const handlePage = () => {
    setPage(page + 1)
  }

  const toggleFilters = () => {
    setFilterOpen(!filterOpen)
  }

  const checkFilter = (filter: any, item: any) => {
    setCheckedFilters({
      ...checkedFilters,
      ...{ [filter.uid]: { key: filter.title, label: item.title, value: item.value } }
    })
  }

  const resetFilters = () => {
    setFilterOpen(false)
    setCheckedFilters({})
    setFilters([])
    router.push(`/${props.prefix}`, undefined, { scroll: false, shallow: true })
  }

  const applyFilters = () => {
    const filters = Object.keys(checkedFilters)
      .filter((key: any) => checkedFilters[key].value)
      .map((key: any) => checkedFilters[key])
    setFilters(filters)
    setFilterOpen(false)
    if (!search) {
      const mainFilter = filters.find((filter: any) => props.result.filters[0].title === filter.key)
      setIsFilterNavigating(true)
      if (mainFilter) router.push(`/${props.prefix}/${mainFilter.value || ''}`, undefined, { scroll: false })
      else router.push(`/${props.prefix}`, undefined, { scroll: false })
    }
  }
  const handleFilter = (item: any) => {
    let value

    if (filters.map((filter: any) => filter.key).indexOf(item.key) > -1)
      value = [...filters.filter((filter: any) => filter.key !== item.key), item.value && item].filter(
        (item: any) => item
      )
    else value = [...filters, item.value && item]

    let mainFilter = value.find((filter: any) => props.result.filters[0].title === filter.key)

    setFilters(value.filter((v: any) => v))

    if (publicRuntimeConfig?.allListingFiltersChangeURL) {
      mainFilter = { value: value.map((filter: any) => filter.value).join(':') }
    }

    if (mainFilter) {
      setIsFilterNavigating(true)
      router.push(`/${props.prefix}/${mainFilter.value || ''}${keyword ? `?keyword=${keyword}` : ``}`, undefined, {
        scroll: false
      })
    }

    if (value.length === 0 || !value[0].value) {
      setIsFilterNavigating(true)
      router.push(`/${props.prefix}`, undefined, { scroll: false })
    }
  }

  // Check if the keyword exists in certain fields
  const keywordInFields = (product: any, keyword: string) => {
    const fields = ['title', 'description', 'species', 'product_code', 'gtin'];
    return fields.some(field => {
      return product[field] && (product?.[field]?.toLowerCase() || '').includes(keyword.toLowerCase())
    });
  }

  // Use fetched items if available (Phase 2 client-side fetch), otherwise fall back to SSG props.items.
  // During client-side navigation between filter URLs (e.g. /recipes → /recipes/fully-cooked),
  // Next.js briefly sets props.items to undefined while the new page data loads. Without a
  // stable reference, sourceItems becomes undefined, items becomes empty, showResults becomes
  // false, and the user sees a "not found" flash before results appear.
  // stableItems holds the last non-null props.items so navigation transitions render the
  // previous list rather than nothing.
  const stableItemsRef = React.useRef<any[]>(props.items)
  if (props.items) stableItemsRef.current = props.items
  const sourceItems = fetchedItems || props.items || stableItemsRef.current

  const items = React.useMemo(() => {
    const activeFilterValues = filters.map((filter: any) => filter.value)
    return sourceItems
      ?.filter((product: any) => !keyword ||
        keywordInFields(product, keyword as string)
      ).filter(
        (product: any) => activeFilterValues.length === 0 ||
          activeFilterValues
            // Bypass the 'unprocessed' items if it's included in the filters array and the "gPCKeywordMapping" config exists
            // The items should be already filtered in a previous step.
            // Check {apps/foodservice/pages/products/[filterId]/index.tsx]} for more info
            .every((filter: string) => (publicRuntimeConfig?.gPCKeywordMapping && filter === unprocessedText)
              || product.filters?.map((f: any) => f.value).includes(filter)
            ))
      .sort((a: any, b: any) => (a.sorting_rank > b.sorting_rank ? -1 : 1))
  }, [sourceItems, keyword, filters, publicRuntimeConfig?.gPCKeywordMapping, unprocessedText])

  const mainFilterValue = router.query?.slug?.[0] || router.query.filterId

  const mainFilterGroup =
    mainFilterValue &&
    props.result?.filters?.find((filterGroup: any) =>
      filterGroup.items.find((filter: any) => filter.value === mainFilterValue)
    )

  const mainFilter = mainFilterGroup && mainFilterGroup?.items?.find((item: any) => item.value === mainFilterValue)

  const pageTitle = React.useMemo((): string => {
    let title: string = props.result?.title ?? '';
    if (mainFilter) {
      title = `${mainFilter.title} ${title}`;
    }
    const shouldSplitFilterId = router.query?.filterId?.toString().includes(':');
    if (shouldSplitFilterId) {
      const filterId = router.query?.filterId?.toString() || '',
        splitFilterId = filterId.split(':');
      title = '';
      splitFilterId.forEach(filterValue => {
        const filterGroup =
          filterValue &&
          props.result?.filters?.find((filterGroup: any) => {
            if (filterGroup.title === 'Species') {
              return filterGroup.items.find((filter: any) => filter.value === filterValue)
            }
          })

        const filter = filterGroup && filterGroup?.items?.find((item: any) => item.value === filterValue)
        if (filter) {
          title += `${filter.title} `;
        }
      })
      title += (props.result?.title ?? '').trim();
    }
    return title;
  }, [props.result, mainFilter, router.query?.filterId])

  const take = page * (props.result?.page_size || 6)
  const current = take >= items?.length ? items?.length : take
  const total = items?.length
  // Suppress "no results" during filter navigation — items can briefly be empty
  // while the new ISR page data loads after a filter selection.
  const showResults = props.result && (isFilterNavigating || (items?.length ?? 0) > 0) && (!search || keyword)

  return (
    <div>
      {search ? (
        <SearchWrapper>
          <Search
            {...{ setSearchOpen: () => { }, translations: props.translations }}
            contained={true}
            path={`/${props.prefix}`}
          />
          <div style={{ paddingTop: 124 }}>
            {search && (
              <h2 style={{ margin: '0 0 40px' }}>
                {props.translations?.search?.results_for} &apos;{keyword}&apos;
              </h2>
            )}
            <Tabs style={{ display: 'flex' }}>
              <Tab
                onClick={() => router.push(`/search?type=products&keyword=${keyword}`)}
                onMouseEnter={() => router.prefetch(`/search?type=products&keyword=${keyword}`)}
                selected={props.prefix === 'products'}>
                 {props.translations?.search?.products_tab}
              </Tab>
              <Tab
                onClick={() => router.push(`/search?type=recipes&keyword=${keyword}`)}
                onMouseEnter={() => router.prefetch(`/search?type=recipes&keyword=${keyword}`)}
                selected={props.prefix === 'recipes'}>
                 {props.translations?.search?.recipes_tab}
              </Tab>
            </Tabs>
          </div>
        </SearchWrapper>
      ) : (
        props.result?.background?.url && (
          <Splash background={props.result?.background?.url}>
            <span>{props.result?.background?.description}</span>
          </Splash>
        )
      )}

      {props.result?.featured_products?.product?.length > 0 && (
        <ListingHeader>
          <Grid>
            <Cell md={12} height={64}>
              <h2 style={{ margin: 0 }}>{pageTitle}</h2>
              <h3>{props.translations?.product?.featured_products}</h3>
              {props.result?.featured_products &&
                <FeaturedProducts items={props.result?.featured_products.product} />}
            </Cell>
          </Grid>
        </ListingHeader>
      )}

      <ListingHeader>
        <Grid>
          <Cell md={6} height={64}>
            <FilterContainer>
              {/* TODO: Put back this condition if needed, commenting for now so the title is displayed */}
              {/* {!search && props.result?.featured_products?.product?.length === 0 && ( */}
              <h2 style={{ margin: 0, fontSize: '2rem' }}
                title={pageTitle}
                dangerouslySetInnerHTML={{ __html: pageTitle }}></h2>
              {/* )} */}
              <FilterMobile onClick={toggleFilters}>
                <img src="/images/filter.svg" width={22} height={22} alt="filter" aria-hidden="true" />{' '}
                {props.translations?.listings?.filter}
              </FilterMobile>
              {filterOpen && (
                <FilterOverlay>
                  <h3>
                    <span>{props.translations?.listings?.filters}</span>
                    <button onClick={toggleFilters}>
                      <img src="/images/close-light.svg" width={31} height={30} />
                    </button>
                  </h3>
                  <FilterList>
                    {props.result?.filters?.map((filter: any) => {
                      if (filter.hasOwnProperty('show_filter_dropdown') &&
                        !filter?.show_filter_dropdown) {
                        return '';
                      }
                      return (
                        <CollapsablePanel
                          title={filter.title}
                          key={filter.value}
                          filter={true}
                          defaultOpen={Object.keys(checkedFilters).find((key: any) => filter.uid === key)}>
                          {[...[{ key: filter.title, title: filter.all_label, value: '' }], ...filter.items].map(
                            (item: any, index: number) => (
                              <FilterOption
                                key={item.value}
                                onClick={() => checkFilter(filter, item)}
                                checked={
                                  Object.keys(checkedFilters).find(
                                    (key: any) => filter.uid === key && checkedFilters[key].value === item.value
                                  ) ||
                                  (!Object.keys(checkedFilters).find((key: any) => filter.uid === key) && index === 0)
                                }>
                                {ReactHtmlParser(item.title)}
                              </FilterOption>
                            )
                          )}
                        </CollapsablePanel>
                      )
                    })}
                  </FilterList>
                  <FilterActions>
                    <FilterAction onClick={resetFilters}>{props.translations?.listings?.clear_filter}</FilterAction>
                     <FilterAction onClick={applyFilters}>{props.translations?.listings?.apply_filter}</FilterAction>
                  </FilterActions>
                </FilterOverlay>
              )}
            </FilterContainer>
          </Cell>
          <Cell md={6}>
            <FilterGroup>
              {props.result?.labels?.filter_by}
              <div style={{ display: 'flex' }}>
                {props.result?.filters?.map((filter: any) => {
                  if (filter.hasOwnProperty('show_filter_dropdown') &&
                    !filter?.show_filter_dropdown) {
                    return '';
                  }
                  return (
                    <NoSSR key={filter.uid}>
                      <Filter
                        classNamePrefix="filter"
                        index
                        value={(() => {
                          const foundItem = filters.find((item: any) => item.key === filter.title);

                          if (foundItem) {
                            return {
                              ...foundItem,
                              label: ReactHtmlParser(foundItem.label)
                            };
                          }

                          return (mainFilterGroup &&
                            filter.title === mainFilterGroup.title && {
                            key: filter.title,
                            label: ReactHtmlParser(mainFilter.title),
                            value: mainFilter.value
                          }) || null;
                        })()}
                        isSearchable={false}
                        placeholder={filter.title}
                        options={[
                          ...[
                            {
                              key: filter.title,
                              label: filter.all_label,
                              value: ''
                            }
                          ],
                          ...filter.items.map((item: any) => ({
                            key: filter.title,
                            label: ReactHtmlParser(item.title),
                            value: item.value
                          }))
                        ]}
                        style={{ width: 200 }}
                        components={{ IndicatorSeparator: null }}
                        onChange={handleFilter}
                      />
                    </NoSSR>
                  )
                })}
              </div>
            </FilterGroup>
          </Cell>
        </Grid>
      </ListingHeader>
      {!showResults && (
        <div style={{ color: 'var(--text)', padding: '40px', textAlign: 'center' }}>
          <h3>{props.result?.labels?.not_found}</h3>
          <p>{props.result?.labels?.not_found_description}</p>
        </div>
      )}
      {showResults && (
        <>
          <ListingWrapper>
            {items.slice(0, current).map((product: any, index: number) => (
              <React.Fragment key={`result-${index}`}>
                {featured_recipe && filters.length === 0 && index === 3 && (
                  <ListingFeaturedContent product={props.prefix === 'products'}>
                    <FeaturedRecipe recipe={featured_recipe} translations={props.translations} />
                  </ListingFeaturedContent>
                )}
                {featured_content && featured_content.title && featured_content.background &&
                  filters.length === 0 && index === 3 && (
                    <ListingFeaturedContent>
                      <FeaturedContent content={featured_content} />
                    </ListingFeaturedContent>
                  )}
                <props.card
                  key={product.uid}
                  {...product}
                  useInfiniteScroll={props?.useInfiniteScroll}
                  isLast={index === current - 1}
                  setScrollLimit={handlePage}
                  prefix={props.prefix} />
              </React.Fragment>
            ))}
          </ListingWrapper>
          <div
            style={{
              color: 'var(--text)',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              margin: '0 0 100px 0'
            }}>
            {isFetchingItems && (
              <div style={{ margin: '0 0 24px 0', opacity: 0.6 }}>
                {props.translations?.listings?.loading || 'Loading products...'}
              </div>
            )}
            {!props?.useInfiniteScroll &&
              <>
                <div style={{ margin: '0 0 24px 0' }}>
                  {props.result?.labels?.pagination?.interpolate({ current, total })}
                </div>
                {items.length > current && <Button onClick={handlePage}>{props.translations?.show_more}</Button>}
              </>
            }
          </div>
        </>
      )}
      {props.result?.reusable_content?.map((entry: any) => (
        <Render
          key={entry.uid}
          pageComponents={entry.page_components?.components}
          contentTypeUid="page"
          translations={props.translations}
          entryUid={props.result?.uid}
          locale={props.result?.locale}
        />
      ))}
    </div>
  )
}

export default Listing
